// D9-03 / D9-02 (#408) — the ONE work-order create-and-edit Sheet, reused by
// three entry points: the list page header (plan 09-08), the global
// quick-add (plan 09-13) and the detail page's "Edit details" (plan 09-09).
// Absent `workOrder` prop -> create mode; present -> edit mode.
//
// Create mode runs the D9-02 post-create chain (confirm, then check-in when
// the on-site switch below is ON) and ALWAYS navigates to the new work
// order's detail page, in every outcome — a stopped chain must still be
// visible, with the state actually reached and its own next legal primary
// on screen, never hidden behind a silently-swallowed toast.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, type SubmitErrorHandler, type SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { typedResolver } from '@/lib/typedResolver';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@marine/ui/sheet';
import { Button } from '@marine/ui/button';
import { Input } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import { ScrollArea } from '@marine/ui/scroll-area';
import { Separator } from '@marine/ui/separator';
import { Switch } from '@marine/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@marine/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@marine/ui/tooltip';
import { ComboboxUser, type ComboboxUserItem } from '@/components/combobox/user';
import { useEmployees } from '@/modules/tenant/hooks/useEmployees';
import { useCan, useEmployee } from '@/modules/tenant/hooks/useTenantAuth';
import { useDealershipProfile } from '@/modules/tenant/hooks/useDealershipProfile';
import { useDealershipSettings, type DealershipSettings } from '@/modules/crm/hooks/useDealershipSettings';
import { useLocations } from '@/modules/inventory/hooks/useLocations';
import { CustomerSearchCombobox } from '@/modules/crm/components/CustomerSearchCombobox';
import { QuickCreateCustomerDialog } from '@/modules/crm/components/QuickCreateCustomerDialog';
import { CustomerBoatQuickCreateDialog } from '@/modules/crm/components/CustomerBoatQuickCreateDialog';
import type { Customer } from '@/modules/crm/types/crm.types';
import {
  useCreateWorkOrder,
  useUpdateWorkOrder,
  useConfirmWorkOrder,
  useCheckInWorkOrder,
  useInvalidateWorkOrder,
} from '../hooks/useWorkOrders';
import { useVerbChain, type ChainStep } from '../hooks/useVerbChain';
import { serviceErrorMessage, applyFieldErrors } from '../utils/serviceErrors';
import { toDealerLocalDateInput, todayLocalDate } from '../utils/dates';
import { isBillingClosed } from '../utils/editLock';
import type { CreateWorkOrderRequest, UpdateWorkOrderRequest, WorkOrder } from '../types/service.types';
import { createWorkOrderSchema, type CreateWorkOrderFormData } from './workOrderSchema';
import { BoatSelect } from './BoatSelect';
import { MoneyInput } from './MoneyInput';
import { formatCents, parseDecimalToCents } from '../utils/money';

type WorkOrderFormValues = CreateWorkOrderFormData;
type AuthorisationMethod = NonNullable<CreateWorkOrderRequest['authorisation_method']>;

const AUTHORISATION_METHOD_OPTIONS: { value: AuthorisationMethod; label: string }[] = [
  { value: 'in_person', label: 'In person' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'portal', label: 'Portal' },
];

const UNSET_AUTHORISATION_METHOD = '__unset__';

const BILLING_CLOSED_HINT = 'Invoiced — billing corrections go through reverse-and-reissue.';

interface WorkOrderFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workOrder?: WorkOrder;
  onCreated?: (id: string) => void;
}

export interface DepositPolicyPreviewInput {
  kind: 'none' | 'percentage' | 'fixed' | undefined;
  percentage: number | null | undefined;
  fixedCents: number | null | undefined;
}

/**
 * D12-13 / D12-05 (#408) — mirrors `WorkOrderService::deriveDepositRequiredCents()`
 * and `CentsDecimalConverter::percentageOfCents()` branch-for-branch. This is
 * a PREVIEW only; the value the server stores and returns is authoritative.
 * Integer cents throughout with an explicit half-up remainder bump — naively
 * rounding the raw floating-point product of cents and percentage (then
 * dividing by 100 in a single expression) diverges from the server on the .5
 * boundary. `deposit_policy_percentage` arrives on THIS wire as a NUMBER
 * (the server reads a decimal STRING), so it is converted to integer
 * hundredths-of-a-percent before multiplying.
 */
export function previewDepositRequiredCents(
  policy: DepositPolicyPreviewInput,
  estimateCents: number | null,
): number {
  const derived = (() => {
    if (policy.kind === 'fixed') {
      return policy.fixedCents ?? 0;
    }
    if (policy.kind === 'percentage') {
      if (estimateCents === null) return 0;
      const hundredthsOfPercent = Math.round((policy.percentage ?? 0) * 100);
      const product = estimateCents * hundredthsOfPercent;
      const whole = Math.floor(product / 10000);
      const remainder = product % 10000;
      return remainder * 2 >= 10000 ? whole + 1 : whole;
    }
    return 0;
  })();

  const clamped = estimateCents === null ? derived : Math.min(derived, estimateCents);
  return Math.max(0, clamped);
}

/** D12-13 (#408) — names the policy in force under the Deposit required field. */
function depositPolicyHelperText(settings: DealershipSettings | undefined): string {
  const kind = settings?.deposit_policy_kind ?? 'none';
  if (kind === 'percentage') {
    return `Dealer policy: ${settings?.deposit_policy_percentage ?? 0}% of the estimate. The stored server value is authoritative.`;
  }
  if (kind === 'fixed') {
    return `Dealer policy: fixed ${formatCents(settings?.deposit_policy_fixed_cents ?? 0, settings?.currency_code)}. The stored server value is authoritative.`;
  }
  return 'No dealer deposit policy. The stored server value is authoritative.';
}

/**
 * 12-REVIEW WR-05 (#408) — the ONE place the live preview is derived from
 * the settings query + the estimate input, so the create-mode effect that
 * shows it and the submit-time "did the advisor actually override it?"
 * comparison can never drift apart.
 */
function previewCentsFor(
  settings: DealershipSettings | undefined,
  estimateValue: WorkOrderFormValues['authorised_amount'],
): number {
  const estimateCents =
    estimateValue === null || estimateValue === undefined ? null : parseDecimalToCents(String(estimateValue));

  return previewDepositRequiredCents(
    {
      kind: settings?.deposit_policy_kind,
      percentage: settings?.deposit_policy_percentage,
      fixedCents: settings?.deposit_policy_fixed_cents,
    },
    estimateCents,
  );
}

/**
 * 09-REVIEW CR-05 — `dropoff_date`/`promised_date` are instants stored as
 * DEALER-LOCAL midnight, so the edit prefill converts them to the dealer's
 * `YYYY-MM-DD` (`toDealerLocalDateInput`) — never `slice(0, 10)`, which
 * reads the UTC day and is a day off for every dealer east of UTC.
 */
function buildDefaults(
  workOrder: WorkOrder | undefined,
  defaultAdvisorId: string | null,
  timezone: string | null,
): WorkOrderFormValues {
  return {
    customer_id: workOrder?.customer_id ?? '',
    boat_id: workOrder?.boat_id ?? null,
    location_id: workOrder?.location_id ?? null,
    service_advisor_id: workOrder?.service_advisor_id ?? defaultAdvisorId,
    dropoff_date: toDealerLocalDateInput(workOrder?.dropoff_date, timezone),
    promised_date: toDealerLocalDateInput(workOrder?.promised_date, timezone),
    authorised_amount: workOrder?.authorised_amount ?? null,
    authorisation_method: workOrder?.authorisation_method ?? null,
    deposit_required_cents: workOrder?.deposit_required_cents ?? null,
    boat_on_site: true,
  };
}

// D12-13 + D12-03 (#408) — `deposit_required_cents` is sent ONLY when the
// advisor actually overrode (create) or changed (edit) it, mirroring the
// existing `boat_on_site`-stripping idiom above. The D12-03 override door
// fires on field PRESENCE (`has()`), not value: an always-sent key would
// 403 every save for an advisor without `service.work_order.deposit_override`.
// 12-REVIEW WR-05 — in create mode "overrode" is decided at SUBMIT time by
// comparing the typed cents with the live preview (see `onSubmit`), never
// by a sticky touched flag: an advisor who types and then reverts to the
// preview value has overridden nothing and must not send the key.
function buildCreatePayload(values: WorkOrderFormValues, sendDeposit: boolean): CreateWorkOrderRequest {
  return {
    customer_id: values.customer_id,
    boat_id: values.boat_id ?? null,
    location_id: values.location_id ?? null,
    service_advisor_id: values.service_advisor_id ?? null,
    dropoff_date: values.dropoff_date || null,
    promised_date: values.promised_date || null,
    authorised_amount: values.authorised_amount ?? null,
    authorisation_method: values.authorisation_method ?? null,
    ...(sendDeposit ? { deposit_required_cents: values.deposit_required_cents ?? null } : {}),
  };
}

function buildUpdatePayload(values: WorkOrderFormValues, depositDirty: boolean): UpdateWorkOrderRequest {
  return {
    customer_id: values.customer_id,
    boat_id: values.boat_id ?? null,
    ...(values.location_id ? { location_id: values.location_id } : {}),
    service_advisor_id: values.service_advisor_id ?? null,
    dropoff_date: values.dropoff_date || null,
    promised_date: values.promised_date || null,
    authorised_amount: values.authorised_amount ?? null,
    authorisation_method: values.authorisation_method ?? null,
    ...(depositDirty ? { deposit_required_cents: values.deposit_required_cents ?? null } : {}),
  };
}

export function WorkOrderFormSheet({ open, onOpenChange, workOrder, onCreated }: WorkOrderFormSheetProps) {
  const navigate = useNavigate();
  const isEditing = !!workOrder;
  // D16-39 — once invoiced, the money fields are frozen (keyed on invoice_state, not the aggregate state).
  const billingClosed = !!workOrder?.invoice_state && isBillingClosed(workOrder.invoice_state);
  const [showQuickCreateCustomer, setShowQuickCreateCustomer] = useState(false);
  const [showQuickCreateBoat, setShowQuickCreateBoat] = useState(false);
  // D12-13 (#408) — true once the advisor has typed into Deposit required
  // themselves; gates the live preview (stop overwriting them). It no
  // longer gates the payload (12-REVIEW WR-05): whether the key is sent is
  // decided at submit time against the current preview, see `onSubmit`.
  const [depositTouched, setDepositTouched] = useState(false);

  const currentEmployee = useEmployee();
  const canCreate = useCan('service.work_order.create');
  const canUpdate = useCan('service.work_order.update');
  // 12-REVIEW WR-05 (#408) — the literal the D12-03 door actually checks
  // (`Store`/`UpdateWorkOrderRequest::authorize()`), read here so the field
  // is disabled for an advisor who could only ever get a 403 from it. UX
  // only — the FormRequest stays the authority (useCan's own contract).
  const canOverrideDeposit = useCan('service.work_order.deposit_override');

  const dealershipProfile = useDealershipProfile();
  const dealershipSettings = useDealershipSettings();
  const { data: employeesData } = useEmployees({ is_active: true, per_page: 100 });
  const { data: locations } = useLocations();
  const showLocationField = (locations?.length ?? 0) > 1;

  const createWorkOrder = useCreateWorkOrder();
  const updateWorkOrder = useUpdateWorkOrder();
  const confirmWorkOrder = useConfirmWorkOrder();
  const checkInWorkOrder = useCheckInWorkOrder();
  const invalidateWorkOrder = useInvalidateWorkOrder();
  const verbChain = useVerbChain();

  const timezone = dealershipProfile.data?.timezone ?? null;

  const form = useForm<WorkOrderFormValues>({
    resolver: typedResolver(createWorkOrderSchema),
    defaultValues: buildDefaults(undefined, null, null),
  });

  // 09-REVIEW WR-06 — reset ONLY on the open transition; the record, the
  // default advisor and the dealer zone are read through a ref (synced
  // first) so a background refetch returning a changed row never silently
  // resets an open edit form.
  const defaultsRef = useRef({ workOrder, advisorId: currentEmployee?.id ?? null, timezone });
  useEffect(() => {
    defaultsRef.current = { workOrder, advisorId: currentEmployee?.id ?? null, timezone };
  }, [workOrder, currentEmployee?.id, timezone]);

  useEffect(() => {
    if (!open) return;
    const { workOrder: record, advisorId, timezone: zone } = defaultsRef.current;
    form.reset(buildDefaults(record, advisorId, zone));
    setDepositTouched(false);
  }, [open, form]);

  // 09-REVIEW CR-05 — the dealer zone normally precedes the first open, but
  // on a cold deep-link it can arrive a beat later; re-seed the date prefill
  // once it does, ONLY while the advisor has not touched the form (WR-06's
  // never-wipe-an-entry rule stands).
  const isDirty = form.formState.isDirty;
  useEffect(() => {
    if (!open || !timezone || isDirty) return;
    const { workOrder: record, advisorId } = defaultsRef.current;
    form.reset(buildDefaults(record, advisorId, timezone));
  }, [open, form, timezone, isDirty]);

  const customerId = form.watch('customer_id') || null;
  const boatOnSite = form.watch('boat_on_site');

  /**
   * 09-UAT gap 2 / 09-REVIEW WR-01 (gap pass) — "known" means "renders an
   * error message": every field listed here has a `formState.errors.<field>`
   * paragraph directly under its input in the JSX below (mirrored, never
   * guessed), so a server 422 naming it lands on screen next to the field.
   * `location_id` is conditional on `showLocationField`; `dropoff_date`
   * only mounts on create while the on-site switch is OFF;
   * `authorised_amount`/`authorisation_method`/`deposit_required_cents` now
   * mount in BOTH modes (D12-02/D12-13). `boat_on_site` is UI-only (never
   * sent to the API) and has no message element, so it is deliberately
   * absent. A 422 naming any OTHER field has nowhere to render as a field
   * error, so `applyFieldErrors` below routes it to a toast instead of an
   * invisible `setError`.
   */
  const knownFields: readonly string[] = [
    'customer_id',
    'boat_id',
    'service_advisor_id',
    'promised_date',
    'authorised_amount',
    'authorisation_method',
    'deposit_required_cents',
    ...(showLocationField ? ['location_id'] : []),
    ...(!isEditing && !boatOnSite ? ['dropoff_date'] : []),
  ];

  // Create mode only — the switch decides the drop-off date, not the advisor.
  useEffect(() => {
    if (!open || isEditing) return;
    if (boatOnSite) {
      form.setValue('dropoff_date', todayLocalDate(dealershipProfile.data?.timezone), {
        shouldDirty: true,
      });
    }
  }, [open, isEditing, boatOnSite, dealershipProfile.data?.timezone, form]);

  // D12-13 (#408) — CREATE mode only: recompute the live deposit preview as
  // the advisor types the estimate, using `shouldDirty: false` so it never
  // looks like the advisor's own edit. Stops the instant `depositTouched`
  // flips true (set by the Deposit required MoneyInput's own onChange
  // below) — the advisor's typed number is never overwritten afterwards.
  const estimateValue = form.watch('authorised_amount');
  useEffect(() => {
    if (!open || isEditing || depositTouched) return;
    form.setValue('deposit_required_cents', previewCentsFor(dealershipSettings.data, estimateValue), {
      shouldDirty: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditing, depositTouched, estimateValue, dealershipSettings.data, form]);

  const advisorItems: ComboboxUserItem[] = useMemo(
    () =>
      (employeesData?.items ?? []).map((employee) => ({
        id: employee.id,
        name: employee.full_name || employee.name,
        email: employee.email,
        avatar: employee.avatar_url ?? undefined,
        status: employee.is_active ? 'online' : 'offline',
      })),
    [employeesData],
  );

  const initialCustomer: Customer | undefined = useMemo(() => {
    if (!workOrder?.customer) return undefined;
    const source = workOrder.customer;
    return {
      id: source.id ?? workOrder.customer_id ?? '',
      first_name: source.first_name ?? '',
      last_name: source.last_name ?? '',
      company_name: source.company_name ?? undefined,
      // 09-REVIEW WR-04 (gap pass) — `CustomerSummary.email` is nullable on the
      // wire (the column has been nullable since 2026-02-11); the CRM
      // `Customer` type models absence as `undefined`.
      email: source.email ?? undefined,
      owner_id: null,
    } as Customer;
  }, [workOrder]);

  const isPending = createWorkOrder.isPending || updateWorkOrder.isPending;

  const onSubmit: SubmitHandler<WorkOrderFormValues> = async (values) => {
    if (isEditing && workOrder?.id) {
      const workOrderId = workOrder.id;
      try {
        await updateWorkOrder.mutateAsync({
          id: workOrderId,
          data: buildUpdatePayload(values, !billingClosed && !!form.formState.dirtyFields.deposit_required_cents),
        });
        toast.success('Work order updated');
        onOpenChange(false);
      } catch (error) {
        const mapped = serviceErrorMessage(error, 'update the work order');
        if (mapped.fieldErrors) {
          const unmapped = applyFieldErrors(form.setError, mapped.fieldErrors, knownFields);
          if (unmapped.length > 0) {
            toast.error(unmapped.join(' '));
          }
          return;
        }
        toast.error(mapped.message);
        // D9-09 — a 409 WORK_ORDER_NOT_EDITABLE (or a changed-since-opened
        // 422) means the row is locked; keeping the Sheet open would only
        // invite a second doomed attempt, so close it on any refetch-worthy
        // refusal AND refetch (09-REVIEW WR-04) so the detail page shows the
        // state the toast just named rather than the stale one.
        if (mapped.refetch) {
          onOpenChange(false);
          invalidateWorkOrder(workOrderId);
        }
      }
      return;
    }

    // 12-REVIEW WR-05 (#408) — decided HERE, at submit time, against the
    // preview the Sheet would show for the submitted estimate: the key is
    // sent only by an advisor who holds the override literal AND left a
    // figure that differs from that preview. A cleared field (null) means
    // "use the policy", which is exactly what omitting the key already does.
    const typedDepositCents = values.deposit_required_cents ?? null;
    const depositOverridden =
      canOverrideDeposit &&
      typedDepositCents !== null &&
      typedDepositCents !== previewCentsFor(dealershipSettings.data, values.authorised_amount);

    let created: WorkOrder;
    try {
      created = await createWorkOrder.mutateAsync(buildCreatePayload(values, depositOverridden));
    } catch (error) {
      const mapped = serviceErrorMessage(error, 'create the work order');
      if (mapped.fieldErrors) {
        const unmapped = applyFieldErrors(form.setError, mapped.fieldErrors, knownFields);
        if (unmapped.length > 0) {
          toast.error(unmapped.join(' '));
        }
        return;
      }
      toast.error(mapped.message);
      return;
    }

    onOpenChange(false);

    if (created.id) {
      const workOrderId = created.id;
      onCreated?.(workOrderId);

      const steps: ChainStep[] = [];
      if (canUpdate) {
        steps.push({
          label: 'confirm the work order',
          mutateAsync: () => confirmWorkOrder.mutateAsync(workOrderId),
        });
        if (values.boat_on_site) {
          steps.push({
            label: 'check in the boat',
            mutateAsync: () => checkInWorkOrder.mutateAsync(workOrderId),
          });
        }
      }

      if (steps.length === 0) {
        // D9-08 — the caller could create but holds neither confirm nor
        // check-in; issuing either request would just 403. Say so plainly
        // instead of pretending the chain ran.
        toast.success('Work order created — ask your manager to confirm it.');
      } else {
        const result = await verbChain.run(steps);
        if (result.stopped) {
          toast.error(result.error ?? 'Failed to update the work order.');
        } else {
          toast.success('Work order created');
        }
      }

      navigate(`/service/work-orders/${workOrderId}`);
    }
  };

  const onInvalid: SubmitErrorHandler<WorkOrderFormValues> = () => {
    toast.error('Please fix the highlighted fields.');
  };

  const submitLabel = isPending ? 'Saving...' : isEditing ? 'Save changes' : 'Create work order';
  const submitDisabled = isPending || (!isEditing && !canCreate);

  const submitButton = (
    <Button type="submit" size="sm" disabled={submitDisabled}>
      {submitLabel}
    </Button>
  );

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="gap-0 lg:w-[640px] sm:max-w-none inset-5 border start-auto h-auto rounded-lg p-0 [&_[data-slot=sheet-close]]:top-4.5 [&_[data-slot=sheet-close]]:end-5">
          <SheetHeader className="border-b py-3 px-5 border-border">
            <SheetTitle className="text-sm font-medium">
              {isEditing ? 'Edit work order details' : 'Create work order'}
            </SheetTitle>
          </SheetHeader>

          <form onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
            <SheetBody className="p-0 grow">
              <ScrollArea className="flex flex-col h-[calc(100dvh-10rem)] mx-1.5">
                <div className="space-y-5 px-5 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="customer_id">
                      Customer <span className="text-destructive">*</span>
                    </Label>
                    <CustomerSearchCombobox
                      value={form.watch('customer_id')}
                      onChange={(id) => {
                        const nextId = id || '';
                        if (nextId !== form.getValues('customer_id')) {
                          // 09-REVIEW WR-01 (gap pass) — a boat belongs to exactly one
                          // customer (`WorkOrderService::assertBoatBelongsToCustomer`), so
                          // a boat picked under the previous customer is stale the moment
                          // the customer changes; clearing it here is what stops the
                          // `boat_id` 422 from ever being sent.
                          form.setValue('boat_id', null, { shouldDirty: true });
                          form.clearErrors('boat_id');
                        }
                        form.setValue('customer_id', nextId, { shouldDirty: true, shouldValidate: true });
                      }}
                      onQuickCreate={() => setShowQuickCreateCustomer(true)}
                      initialCustomer={initialCustomer}
                    />
                    {form.formState.errors.customer_id && (
                      <p className="text-xs text-destructive">{form.formState.errors.customer_id.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="boat_id">Boat</Label>
                    <BoatSelect
                      customerId={customerId}
                      value={form.watch('boat_id') ?? null}
                      onChange={(boatId) => {
                        form.setValue('boat_id', boatId, { shouldDirty: true });
                        form.clearErrors('boat_id');
                      }}
                      onAddBoat={() => setShowQuickCreateBoat(true)}
                    />
                    {form.formState.errors.boat_id && (
                      <p className="text-xs text-destructive">{form.formState.errors.boat_id.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="service_advisor_id">Advisor</Label>
                    <ComboboxUser
                      id="service_advisor_id"
                      users={advisorItems}
                      value={form.watch('service_advisor_id') ?? null}
                      onChange={(id) => form.setValue('service_advisor_id', id, { shouldDirty: true })}
                      includeUnassigned
                      unassignedLabel="Unassigned"
                      placeholder="Select an advisor..."
                    />
                    {form.formState.errors.service_advisor_id && (
                      <p className="text-xs text-destructive">
                        {form.formState.errors.service_advisor_id.message}
                      </p>
                    )}
                  </div>

                  {showLocationField && (
                    <div className="space-y-2">
                      <Label htmlFor="location_id">Location</Label>
                      <Select
                        value={form.watch('location_id') ?? undefined}
                        onValueChange={(next) => form.setValue('location_id', next, { shouldDirty: true })}
                      >
                        <SelectTrigger id="location_id" aria-label="Location">
                          <SelectValue placeholder="Select a location" />
                        </SelectTrigger>
                        <SelectContent>
                          {(locations ?? []).map((location) => (
                            <SelectItem key={location.id} value={location.id}>
                              {location.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {form.formState.errors.location_id && (
                        <p className="text-xs text-destructive">{form.formState.errors.location_id.message}</p>
                      )}
                    </div>
                  )}

                  {!isEditing && (
                    <>
                      <Separator />
                      <div className="flex items-center justify-between rounded-md border border-border p-3">
                        <div className="space-y-0.5 pe-4">
                          <Label htmlFor="boat_on_site">Boat is on site now</Label>
                          <p className="text-xs text-muted-foreground">
                            On confirms and checks the boat in today. Off books it for a later drop-off.
                          </p>
                        </div>
                        <Switch
                          id="boat_on_site"
                          checked={boatOnSite}
                          onCheckedChange={(checked) =>
                            form.setValue('boat_on_site', checked, { shouldDirty: true })
                          }
                        />
                      </div>
                      {!boatOnSite && (
                        <div className="space-y-2">
                          <Label htmlFor="dropoff_date">Expected drop-off</Label>
                          <Input id="dropoff_date" type="date" {...form.register('dropoff_date')} />
                          {form.formState.errors.dropoff_date && (
                            <p className="text-xs text-destructive">{form.formState.errors.dropoff_date.message}</p>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="promised_date">Promised date</Label>
                    <Input id="promised_date" type="date" {...form.register('promised_date')} />
                    {form.formState.errors.promised_date && (
                      <p className="text-xs text-destructive">{form.formState.errors.promised_date.message}</p>
                    )}
                  </div>

                  {/* D12-02 (#408, DEP-02) — this block used to be gated
                      behind `isEditing`. A `percentage` deposit policy has
                      no basis at create time unless the advisor can enter
                      an estimate, so gating it that way meant every new
                      work order under a percentage policy silently derived
                      a $0.00 deposit (RESEARCH Pitfall 5). Unconditional
                      here makes that structurally impossible rather than
                      merely documented. */}
                  <Separator />
                  <div className="space-y-2">
                    <Label htmlFor="authorised_amount">
                      {isEditing ? 'Authorised amount' : 'Estimated amount (optional)'}
                    </Label>
                    <Input
                      id="authorised_amount"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      disabled={billingClosed}
                      {...form.register('authorised_amount')}
                    />
                    {billingClosed && <p className="text-xs text-muted-foreground">{BILLING_CLOSED_HINT}</p>}
                    <p className="text-xs text-muted-foreground">
                      {isEditing
                        ? 'Decimal dollars the customer pre-authorised for this work order.'
                        : 'The estimate the dealer deposit policy is calculated from.'}
                    </p>
                    {form.formState.errors.authorised_amount && (
                      <p className="text-xs text-destructive">
                        {form.formState.errors.authorised_amount.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="authorisation_method">Authorisation method</Label>
                    <Select
                      value={form.watch('authorisation_method') ?? UNSET_AUTHORISATION_METHOD}
                      onValueChange={(next) =>
                        form.setValue(
                          'authorisation_method',
                          next === UNSET_AUTHORISATION_METHOD ? null : (next as AuthorisationMethod),
                          { shouldDirty: true },
                        )
                      }
                    >
                      <SelectTrigger id="authorisation_method" aria-label="Authorisation method">
                        <SelectValue placeholder="Not set" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={UNSET_AUTHORISATION_METHOD}>Not set</SelectItem>
                        {AUTHORISATION_METHOD_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {form.formState.errors.authorisation_method && (
                      <p className="text-xs text-destructive">
                        {form.formState.errors.authorisation_method.message}
                      </p>
                    )}
                  </div>

                  {/* D12-13 (#408, DEP-02) — the advisor sees the dealer's
                      policy-derived deposit the instant they type the
                      estimate (create) or the work order's stored figure
                      (edit), and can type over it in the same breath. */}
                  <div className="space-y-2">
                    <Label htmlFor="deposit_required_cents">Deposit required</Label>
                    <MoneyInput
                      id="deposit_required_cents"
                      aria-label="Deposit required"
                      disabled={!canOverrideDeposit || billingClosed}
                      value={form.watch('deposit_required_cents') ?? null}
                      onChange={(cents) => {
                        setDepositTouched(true);
                        form.setValue('deposit_required_cents', cents, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                    />
                    <p className="text-xs text-muted-foreground">
                      {depositPolicyHelperText(dealershipSettings.data)}
                    </p>
                    {billingClosed && <p className="text-xs text-muted-foreground">{BILLING_CLOSED_HINT}</p>}
                    {!canOverrideDeposit && !billingClosed && (
                      <p className="text-xs text-muted-foreground">
                        Shown from the dealer policy; you do not have permission to override it.
                      </p>
                    )}
                    {form.formState.errors.deposit_required_cents && (
                      <p className="text-xs text-destructive">
                        {form.formState.errors.deposit_required_cents.message}
                      </p>
                    )}
                  </div>
                </div>
              </ScrollArea>
            </SheetBody>

            <SheetFooter className="flex-row border-t py-2.5 px-5 border-border gap-2.5">
              <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              {!isEditing && !canCreate ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0}>{submitButton}</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    You don&apos;t have permission to create work orders. Ask your manager.
                  </TooltipContent>
                </Tooltip>
              ) : (
                submitButton
              )}
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <QuickCreateCustomerDialog
        open={showQuickCreateCustomer}
        onOpenChange={setShowQuickCreateCustomer}
        onSuccess={(customerId) => {
          form.setValue('customer_id', customerId, { shouldValidate: true, shouldDirty: true });
          form.clearErrors('customer_id');
        }}
      />

      {/* 09-UAT gap 1 — this dialog MUST stay outside the <form> above; mounted
          inside it, its own submit bubbled through the Radix portal and fired
          POST /api/work-orders on every Add-boat click. */}
      {customerId && (
        <CustomerBoatQuickCreateDialog
          open={showQuickCreateBoat}
          onOpenChange={setShowQuickCreateBoat}
          customerId={customerId}
          onSuccess={(boatId) => form.setValue('boat_id', boatId, { shouldDirty: true })}
        />
      )}
    </>
  );
}
